import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Select,
  Stack,
  TextField,
  Typography,
  toast,
} from '@montugroup/design-system';
import { Grid2, MenuItem, styled } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { UnableToDispenseReasonLabel } from '@/components/order/order.types';
import { usePharmacistSplitOrderModal } from '@/context/orders/PharmacistOrdersModals';
import { datadogRum } from '@datadog/browser-rum';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { useQueryClient } from '@tanstack/react-query';
import { DispenseIntervalSection } from '../unable-to-dispense/DispenseIntervalSection';
import type { OkToDispenseOrderProduct } from '../unable-to-dispense/unable-to-dispense.types';

const ContentLayout = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

type ReasonOption = {
  name: string;
  value: number;
};

type SelectedReasons = {
  [key: string | number]: {
    label: string;
    value: number;
    otherReason?: string;
  };
};

const client = getTsPharmacyClient();

function SplitOrderModal() {
  const queryClient = useQueryClient();
  const { unableToDispenseReasons, splitOrderModal, hideSplitOrderModal, saveUnableToDispense } =
    usePharmacistSplitOrderModal();
  const [reasonOptions, setReasonOptions] = useState<ReasonOption[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<SelectedReasons>({});
  const [additionalComments, setAdditionalComments] = useState<string>('');
  const [disabled, setDisabled] = useState<boolean>(false);
  const { orderDetails } = splitOrderModal;

  const dispenseIntervalProducts =
    orderDetails?.productDetails.filter(
      (product) => selectedReasons[product.id]?.label === UnableToDispenseReasonLabel.IntervalTime,
    ) ?? [];

  // NOTE: duplicating the okToDispenseProducts logic here just for feature launch analytics
  const dispenseIntervalProductIds = dispenseIntervalProducts.map((product) => Number(product.id));
  const { data } = client.getOrderDispenseIntervals.useQuery({
    queryKey: [...client.getOrderDispenseIntervals.keys, orderDetails?.orderCode],
    queryData: {
      params: {
        orderCode: orderDetails?.orderCode as string,
      },
    },
    enabled: !!orderDetails?.orderCode,
  });
  const orderProducts = data?.body.data.orderProducts ?? [];
  const okToDispenseProducts = dispenseIntervalProductIds
    .map((productId) => orderProducts.find((orderProduct) => orderProduct.product.id === productId))
    .filter((orderProduct): orderProduct is OkToDispenseOrderProduct => orderProduct?.eligibility === 'ok');

  const shouldShowDispenseIntervalAlert = orderDetails && dispenseIntervalProducts.length > 0;

  const handleReasonChange = (e: SelectChangeEvent<unknown>, productId: number | string) => {
    const selectedReason = reasonOptions.find((option) => option.value === e.target.value);
    if (selectedReason) {
      setSelectedReasons({
        ...selectedReasons,
        [productId]: { label: selectedReason.name, value: selectedReason.value },
      });
    }
  };

  const handleOtherReasonChange = (e: ChangeEvent<HTMLInputElement>, productId: number | string) => {
    setSelectedReasons({
      ...selectedReasons,
      [productId]: {
        ...selectedReasons[productId],
        otherReason: e.target.value,
      },
    });
  };

  const handleAdditionalCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAdditionalComments(e.target.value);
  };

  const handleCancel = () => {
    datadogRum.addAction('utd-cancelled', {
      hasIntervalTimeAlert: shouldShowDispenseIntervalAlert && okToDispenseProducts.length !== 0,
      ...orderDetails,
    });

    resetStateAndClose();
  };

  const resetStateAndClose = () => {
    setDisabled(false);
    setAdditionalComments('');
    setSelectedReasons({});
    hideSplitOrderModal();
  };

  const handleConfirm = async () => {
    setDisabled(true);
    if (!orderDetails?.orderId) {
      toast.error('Save failed, order not found');
      setDisabled(false);
      return;
    }

    const { orderId, productDetails } = orderDetails;
    const productsWithReasons = productDetails.map((product) => ({
      productId: product.id,
      orderId,
      reason: { label: selectedReasons[product.id]?.label, value: selectedReasons[product.id]?.value },
      otherReason: selectedReasons[product.id]?.otherReason,
      additionalComment: additionalComments,
    }));
    const result = await saveUnableToDispense(productsWithReasons);
    datadogRum.addAction('utd-confirmed', {
      hasIntervalTimeAlert: shouldShowDispenseIntervalAlert && okToDispenseProducts.length !== 0,
      ...productsWithReasons,
    });

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: client.listOrders.keys });
      toast.success('Order Updated');
      resetStateAndClose();
    } else {
      toast.error(result.message);
      setDisabled(false);
    }
  };

  useEffect(() => {
    setReasonOptions(unableToDispenseReasons.map((reason) => ({ name: reason.name, value: reason.id })));
  }, [unableToDispenseReasons]);

  return (
    <Dialog open={splitOrderModal.visible} onClose={hideSplitOrderModal} fullWidth maxWidth="lg" disableEscapeKeyDown>
      <DialogTitle>Unable to dispense {orderDetails?.orderCode}</DialogTitle>
      <DialogContent>
        <ContentLayout>
          <Typography>Mark the reason why this order cannot be dispensed</Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 4 }}>
              <Stack spacing={2}>
                <Typography fontWeight="bold">Paid date</Typography>
                <Typography>{orderDetails?.shippedDate || '-'}</Typography>
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 4 }}>
              <Stack spacing={2}>
                <Typography fontWeight="bold">Patient ID</Typography>
                <Typography>{orderDetails?.patientCode || '-'}</Typography>
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 4 }}>
              <Stack spacing={2}>
                <Typography fontWeight="bold">Prescription</Typography>
                <Typography>{orderDetails?.prescriptionNames.join(', ') || '-'} </Typography>
              </Stack>
            </Grid2>
          </Grid2>
          <Divider />
          {orderDetails?.productDetails.map((product) => (
            <Grid2 container spacing={5} key={product.id}>
              <Grid2 size={{ xs: 4 }}>
                <Stack spacing={2}>
                  <Typography fontWeight="bold">Product</Typography>
                  <Typography>{product.name}</Typography>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 4 }}>
                <Stack spacing={2}>
                  <Typography fontWeight="bold">Quantity</Typography>
                  <Typography>{product.quantity}</Typography>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 4 }}>
                <Stack spacing={2}>
                  <Typography fontWeight="bold">Reason</Typography>
                  {reasonOptions.length > 0 && (
                    <Select
                      data-testid="reason-select"
                      onChange={(e) => handleReasonChange(e, product.id)}
                      value={selectedReasons[product.id]?.value}
                    >
                      {reasonOptions.map((option) => (
                        <MenuItem value={option.value} key={option.name}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  {selectedReasons[product.id] &&
                    selectedReasons[product.id].label === UnableToDispenseReasonLabel.Others && (
                      <TextField
                        placeholder="Issue"
                        name="reason"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleOtherReasonChange(e, product.id)}
                      />
                    )}
                </Stack>
              </Grid2>
            </Grid2>
          ))}
          {shouldShowDispenseIntervalAlert && (
            <DispenseIntervalSection
              orderCode={orderDetails.orderCode}
              productIds={dispenseIntervalProducts.map((product) => Number(product.id))}
            />
          )}
          <Grid2 size={{ xs: 12 }}>
            <TextField
              name="additional-info"
              InputLabelProps={{ shrink: true }}
              label="Additional comments"
              placeholder="Any additional comments relating to an issue.."
              multiline
              rows={3}
              fullWidth
              onChange={handleAdditionalCommentChange}
            />
          </Grid2>
        </ContentLayout>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="secondary" onClick={handleCancel} disabled={disabled}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={disabled}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SplitOrderModal;
